import React, {useEffect, useRef, useState} from "react";
import axiosInstance from "../../../../AxiosInstance";
import AdminSidebar from "../../components/sidebar/admin_sidebar";
import PageTitle from "../../../../components/page_title/page_title";
import TableComponent from "../../../../components/table/table";
import Modal from "../../../../components/modal/modal";
import {SelectInput, TextInput} from "../../../../components/input/inputs";
import styles from "../../../settings/styles/settings.module.css";
import sidebarStyles from "../../components/sidebar/admin_sidebar.module.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit} from "@fortawesome/free-solid-svg-icons";
import {useTranslation} from "react-i18next";

const MODBUS_DATA_TYPE_OPTIONS = [
    {id: 'UINT16',     name: 'UINT16 (16-bit unsigned)'},
    {id: 'INT16',      name: 'INT16 (16-bit signed)'},
    {id: 'UINT32_BE',  name: 'UINT32 Big-Endian'},
    {id: 'INT32_BE',   name: 'INT32 Big-Endian'},
    {id: 'FLOAT32_BE', name: 'FLOAT32 Big-Endian (IEEE 754)'},
    {id: 'UINT32_LE',  name: 'UINT32 Little-Endian (word-swap)'},
    {id: 'INT32_LE',   name: 'INT32 Little-Endian (word-swap)'},
    {id: 'FLOAT32_LE', name: 'FLOAT32 Little-Endian (word-swap)'},
    {id: 'COIL',       name: 'Coil / Discrete input (single bit)'},
];

function fmt(val) {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'number') {
        if (!isFinite(val)) return val > 0 ? '+Inf' : '-Inf';
        if (Number.isNaN(val)) return 'NaN';
        return Math.abs(val) >= 1000 ? val.toLocaleString('en', {maximumFractionDigits: 3})
             : Math.abs(val) < 0.001 && val !== 0 ? val.toExponential(3)
             : parseFloat(val.toPrecision(6)).toString();
    }
    return String(val);
}

function ModbusDecoder({ onUse }) {
    const {t} = useTranslation();
    const [hexInput, setHexInput] = useState('');
    const [startReg, setStartReg] = useState('');
    const [decoded, setDecoded] = useState(null);
    const [error, setError] = useState('');

    const decode = () => {
        setError('');
        setDecoded(null);
        const hex = hexInput.replace(/[\s:]/g, '');
        if (!hex) { setError('Paste a hex frame first'); return; }
        if (hex.length % 2 !== 0) { setError('Odd number of hex characters'); return; }

        let bytes;
        try {
            bytes = new Uint8Array(hex.match(/.{2}/g).map(b => parseInt(b, 16)));
        } catch {
            setError('Invalid hex string'); return;
        }

        // Detect Appendix II encapsulation
        let taskId = null;
        let modbusBytes = bytes;
        const dtuCmd = bytes[0];
        const isUplink = (dtuCmd & 0x80) !== 0;
        const msgType = dtuCmd & 0x1F;

        if (isUplink && msgType === 0x01 && bytes.length >= 7) {
            // Non-transparent: DTU_CMD(1) + MSER(2) + MDATA_LEN(2) + TASK_ID(1) + Modbus RTU
            taskId = bytes[5];
            modbusBytes = bytes.slice(6);
        } else if (isUplink && msgType === 0x02 && bytes.length >= 6) {
            // Transparent reply: no TASK_ID
            modbusBytes = bytes.slice(5);
        }

        if (modbusBytes.length < 5) { setError('Modbus RTU frame too short'); return; }
        const slave = modbusBytes[0];
        const fc = modbusBytes[1];
        const byteCount = modbusBytes[2];

        if (modbusBytes.length < 3 + byteCount) { setError('Byte count mismatch in Modbus frame'); return; }
        const data = modbusBytes.slice(3, 3 + byteCount);
        const start = parseInt(startReg) || 0;

        const registers = [];
        for (let i = 0; i + 1 < data.length; i += 2) {
            const regAddr = start + Math.floor(i / 2);
            const b0 = data[i], b1 = data[i + 1];
            const rawHex = b0.toString(16).padStart(2,'0') + b1.toString(16).padStart(2,'0');

            const uint16 = (b0 << 8) | b1;
            const int16 = uint16 >= 0x8000 ? uint16 - 0x10000 : uint16;

            let uint32be = null, int32be = null, float32be = null;
            let uint32le = null, int32le = null, float32le = null;

            if (i + 3 < data.length) {
                const b2 = data[i + 2], b3 = data[i + 3];
                const buf = new ArrayBuffer(4);
                const view = new DataView(buf);

                view.setUint8(0, b0); view.setUint8(1, b1); view.setUint8(2, b2); view.setUint8(3, b3);
                uint32be = view.getUint32(0);
                int32be  = view.getInt32(0);
                float32be = view.getFloat32(0);

                // Word-swap: low word first in data, high word second
                view.setUint8(0, b2); view.setUint8(1, b3); view.setUint8(2, b0); view.setUint8(3, b1);
                uint32le = view.getUint32(0);
                int32le  = view.getInt32(0);
                float32le = view.getFloat32(0);
            }

            registers.push({ regAddr, rawHex, uint16, int16, uint32be, int32be, float32be, uint32le, int32le, float32le });
        }

        setDecoded({ taskId, slave, fc, byteCount, registers, start });
    };

    const applyValue = (reg, dtype) => {
        onUse({
            modbus_poll_id: decoded.taskId ?? '',
            modbus_start_register: decoded.start,
            modbus_register_address: reg.regAddr,
            modbus_data_type: dtype,
        });
    };

    const tdStyle = { fontSize: '0.82rem', padding: '4px 8px', verticalAlign: 'middle' };
    const btnStyle = { fontSize: '0.75rem', padding: '1px 7px', whiteSpace: 'nowrap' };

    return (
        <div className="card mb-4" style={{border: '1px solid #cbd5e1'}}>
            <div className="card-header py-2" style={{background: '#f1f5f9', fontWeight: 600, fontSize: '0.9rem'}}>
                Modbus Frame Decoder
            </div>
            <div className="card-body pb-2">
                <div className="row g-2 align-items-end mb-3">
                    <div className="col">
                        <label className="form-label mb-1" style={{fontSize: '0.82rem'}}>Frame hex (from LNS "Data hex" field)</label>
                        <input
                            type="text"
                            className="form-control form-control-sm font-monospace"
                            placeholder="e.g. 8109e5000a01010404b66d4979ba63"
                            value={hexInput}
                            onChange={e => setHexInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && decode()}
                        />
                    </div>
                    <div className="col-auto">
                        <label className="form-label mb-1" style={{fontSize: '0.82rem'}}>Start Register <span className="text-muted">(MODBUS ADDRESS)</span></label>
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="e.g. 6"
                            style={{width: '100px'}}
                            value={startReg}
                            onChange={e => setStartReg(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && decode()}
                        />
                    </div>
                    <div className="col-auto">
                        <button className="btn btn-sm btn-primary" onClick={decode}>Decode</button>
                    </div>
                </div>

                {error && <div className="alert alert-danger py-1 mb-2" style={{fontSize: '0.82rem'}}>{error}</div>}

                {decoded && (
                    <>
                        <div className="d-flex gap-3 mb-2" style={{fontSize: '0.82rem', color: '#475569'}}>
                            <span>Slave: <strong>{decoded.slave}</strong></span>
                            <span>FC: <strong>{decoded.fc}</strong></span>
                            <span>Data bytes: <strong>{decoded.byteCount}</strong></span>
                            {decoded.taskId !== null && <span>TASK_ID: <strong className="text-primary">{decoded.taskId}</strong></span>}
                        </div>

                        <div style={{overflowX: 'auto'}}>
                            <table className="table table-bordered table-sm mb-0" style={{fontSize: '0.82rem'}}>
                                <thead style={{background: '#f8fafc'}}>
                                    <tr>
                                        <th style={tdStyle}>Reg (addr)</th>
                                        <th style={tdStyle}>Bytes</th>
                                        <th style={tdStyle}>UINT16</th>
                                        <th style={tdStyle}>INT16</th>
                                        <th style={tdStyle}>FLOAT32 BE</th>
                                        <th style={tdStyle}>FLOAT32 LE ★</th>
                                        <th style={tdStyle}>UINT32 BE</th>
                                        <th style={tdStyle}>INT32 BE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {decoded.registers.map((reg) => (
                                        <tr key={reg.regAddr}>
                                            <td style={{...tdStyle, fontWeight: 600}}>{reg.regAddr}</td>
                                            <td style={{...tdStyle, fontFamily: 'monospace', color: '#64748b'}}>{reg.rawHex}</td>
                                            <td style={tdStyle}>
                                                <div>{fmt(reg.uint16)}</div>
                                                <button className="btn btn-outline-secondary btn-sm mt-1" style={btnStyle} onClick={() => applyValue(reg, 'UINT16')}>Use →</button>
                                            </td>
                                            <td style={tdStyle}>
                                                <div style={reg.int16 < 0 ? {color:'#dc2626'} : {}}>{fmt(reg.int16)}</div>
                                                <button className="btn btn-outline-secondary btn-sm mt-1" style={btnStyle} onClick={() => applyValue(reg, 'INT16')}>Use →</button>
                                            </td>
                                            <td style={tdStyle}>
                                                {reg.float32be !== null ? <>
                                                    <div style={!isFinite(reg.float32be)||isNaN(reg.float32be) ? {color:'#94a3b8'} : {}}>{fmt(reg.float32be)}</div>
                                                    <button className="btn btn-outline-secondary btn-sm mt-1" style={btnStyle} onClick={() => applyValue(reg, 'FLOAT32_BE')}>Use →</button>
                                                </> : <span className="text-muted">—</span>}
                                            </td>
                                            <td style={{...tdStyle, background: '#fffbeb'}}>
                                                {reg.float32le !== null ? <>
                                                    <div style={!isFinite(reg.float32le)||isNaN(reg.float32le) ? {color:'#94a3b8'} : {fontWeight: 500}}>{fmt(reg.float32le)}</div>
                                                    <button className="btn btn-outline-primary btn-sm mt-1" style={btnStyle} onClick={() => applyValue(reg, 'FLOAT32_LE')}>Use →</button>
                                                </> : <span className="text-muted">—</span>}
                                            </td>
                                            <td style={tdStyle}>
                                                {reg.uint32be !== null ? <>
                                                    <div>{fmt(reg.uint32be)}</div>
                                                    <button className="btn btn-outline-secondary btn-sm mt-1" style={btnStyle} onClick={() => applyValue(reg, 'UINT32_BE')}>Use →</button>
                                                </> : <span className="text-muted">—</span>}
                                            </td>
                                            <td style={tdStyle}>
                                                {reg.int32be !== null ? <>
                                                    <div style={reg.int32be < 0 ? {color:'#dc2626'} : {}}>{fmt(reg.int32be)}</div>
                                                    <button className="btn btn-outline-secondary btn-sm mt-1" style={btnStyle} onClick={() => applyValue(reg, 'INT32_BE')}>Use →</button>
                                                </> : <span className="text-muted">—</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <small className="text-muted d-block mt-1">{t('adminDeviceTypes.float32Note')}</small>
                    </>
                )}
            </div>
        </div>
    );
}

function Popup({current, setShowPopup, setShowModal, setForm}) {
    const {t} = useTranslation();
    return (
        <div style={{width: '200px', height: 'auto'}}>
            <div className={styles.popupButton} onClick={() => {
                setShowModal(true);
                setShowPopup(false);
                setForm(current);
            }}>
                <FontAwesomeIcon icon={faEdit} color={'#64748B'} className={'me-2'}/>
                <div style={{color: '#334155'}}>{t('common.edit')}</div>
            </div>
        </div>
    )
}

export default function ListDeviceTypeVariables() {
    const {t} = useTranslation();
    const device_type = JSON.parse(localStorage.getItem('currentDeviceType'));
    const isModbusType = device_type?.is_modbus === true;

    const [rows, setRows] = useState([]);
    const [currentVariable, setCurrentVariable] = useState({});
    const [form, setForm] = useState({device_type_id: device_type.id});
    const [showModal, setShowModal] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const popupRef = useRef(null);
    const [mousePosition, setMousePosition] = useState({ x: null, y: null });

    const cols = [
        {title: t('adminDeviceTypes.variableName'), internal: 'name' },
        ...(isModbusType
            ? [
                {title: t('adminDeviceTypes.mqttColumns.registerAddress'), internal: 'modbus_register_address'},
                {title: t('adminDeviceTypes.mqttColumns.dataType'), internal: 'modbus_data_type'},
              ]
            : [
                {title: t('adminDeviceTypes.mqttColumns.jsonPath'), internal: 'json_path'},
              ]
        ),
        {title: t('adminDeviceTypes.unit'), internal: 'unit' },
        {title: t('adminDeviceTypes.type'), internal: 'data_type'},
        {title: t('adminDeviceTypes.mqttColumns.graphicType'), internal: 'graphic_type', code: (row, data) => {
            if(data === 0) return 'Line';
            else if(data === 1) return 'Bar';
            else if(data === 2) return 'Area';
            else if(data === 3) return 'Radar';
            else if(data === 4) return 'Pie';
            else return 'Unknown';
        }},
        {title: t('admin.columns.actions'), internal: '', code: (row, data) => {
                return (
                    <div className={`d-flex flex-row justify-content-center align-items-center ps-3 ${styles.editButton}`} onClick={(event) => {
                        setShowPopup(true);
                        setMousePosition({ x: event.clientX, y: event.clientY });
                        setCurrentVariable(row);
                        setForm(row);
                    }}/>
                )
        }}
    ];

    const handleOutsideClick = (event) => {
        if (popupRef.current && !popupRef.current.contains(event.target)) {
            setShowPopup(false);
        }
    };

    useEffect(() => {
        if (showPopup) {
            document.addEventListener('mousedown', handleOutsideClick);
        } else {
            document.removeEventListener('mousedown', handleOutsideClick);
        }
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [showPopup]);

    useEffect(() => {
        const tempType = JSON.parse(localStorage.getItem('currentDeviceType'));
        axiosInstance.get('device_type/' + tempType.id + '/').then((res) => {
            setRows(res.data.variables);
        });
    }, []);

    const create = () => {
        const tempForm = {
            ...form,
            device_type_id: device_type.id,
        }
        if(Object.keys(currentVariable).length > 0) {
            axiosInstance.put('device_variable/' + currentVariable.id + '/', tempForm).then(() => {
                window.location.reload();
            });
        } else {
            axiosInstance.post('device_variable/', tempForm).then(() => {
                window.location.reload();
            });
        }
    };

    const handleDecoderUse = (prefilled) => {
        setCurrentVariable({});
        setForm({ device_type_id: device_type.id, ...prefilled });
        setShowModal(true);
    };

    return (
        <div className={sidebarStyles.adminContent}>
            <AdminSidebar active={1}/>

            {showPopup &&
                <div ref={popupRef} className={styles.popUpModule} style={{top: mousePosition.y + 15, left: mousePosition.x - 180, position: 'absolute', zIndex: '100000'}}>
                    <Popup current={currentVariable} setShowPopup={setShowPopup} setShowModal={setShowModal} setForm={setForm}/>
                </div>
            }

            <Modal title={'Edit Device Type Variable'} show={showModal} setter={setShowModal} name={Object.keys(currentVariable).length > 0 ? t('adminDeviceTypes.editVariable') : t('adminDeviceTypes.createVariable')} buttonName={Object.keys(currentVariable).length > 0 ? t('common.save') : t('common.create')} onClick={create}>
                <TextInput name={'name'} form={form} setForm={setForm}/>

                {isModbusType ? (
                    <>
                        <div className="alert alert-secondary py-2 mb-3" style={{fontSize: '0.85rem'}}>
                            <strong>Modbus RTU Register Configuration</strong>
                        </div>
                        <TextInput name={'Poll ID (fPort / TASK_ID)'} internalName={'modbus_poll_id'} form={form} setForm={setForm}/>
                        <TextInput name={'Start Register'} internalName={'modbus_start_register'} form={form} setForm={setForm}/>
                        <TextInput name={'Register Address'} internalName={'modbus_register_address'} form={form} setForm={setForm}/>
                        <div className={'mb-3'}>
                            <SelectInput name={'Modbus Data Type'} internalName={'modbus_data_type'} form={form} setForm={setForm} options={MODBUS_DATA_TYPE_OPTIONS}/>
                        </div>
                        <TextInput name={'Scale (multiplier)'} internalName={'modbus_scale'} form={form} setForm={setForm}/>
                        <TextInput name={'Offset (added after scale)'} internalName={'modbus_offset'} form={form} setForm={setForm}/>
                    </>
                ) : (
                    <TextInput name={'JSON Path'} internalName={'json_path'} form={form} setForm={setForm}/>
                )}

                <TextInput name={'Unit'} internalName={'unit'} form={form} setForm={setForm}/>
                <div className={'mb-3'}>
                    <SelectInput name={'data_type'} form={form} setForm={setForm} options={[{id: 'number', name: 'Number'}, {id: 'string', name: 'String'}, {id: 'boolean', name: 'Boolean'}, {id: 'object', name: 'Object'}, {id: 'array', name: 'Array'}]}/>
                </div>
                {/* Etiquetas de estado por defecto para este canal booleano. La
                    segunda guarda cubre filas antiguas creadas antes de data_type. */}
                {(form.data_type === 'boolean' || Number(form.type) === 5) && (
                    <div className={'mb-3'} style={{display: 'flex', gap: '12px'}}>
                        <div style={{flex: 1}}>
                            <TextInput name={t('variables.trueLabel')} internalName={'true_label'}
                                       placeholder={'True'} form={form} setForm={setForm}/>
                        </div>
                        <div style={{flex: 1}}>
                            <TextInput name={t('variables.falseLabel')} internalName={'false_label'}
                                       placeholder={'False'} form={form} setForm={setForm}/>
                        </div>
                    </div>
                )}
                <div className={'mb-3'}>
                    <SelectInput name={'graphic Type'} internalName={'graphic_type'} form={form} setForm={setForm} options={[{id: 0, name: 'Line'}, {id: 1, name: 'Bar'}, {id: 2, name: 'Area'}, {id: 3, name: 'Radar'}, {id: 4, name: 'Pie'}, {id: 5, name: 'Step (Boolean)'}, {id: 6, name: 'Timeline (String)'}]}/>
                </div>
            </Modal>

            <div className={'mx-5'}>
                <PageTitle title={t('adminDeviceTypes.variablesTitle')} createName={'Device Type Variable'} openModal={() => {
                    setCurrentVariable({});
                    setForm({device_type_id: device_type.id});
                    setShowModal(true);
                }}/>

                {isModbusType && (
                    <div className={'mt-3'}>
                        <ModbusDecoder onUse={handleDecoderUse}/>
                    </div>
                )}

                <div className={'mt-3'}>
                    <TableComponent cols={cols} rows={rows} pull={setRows}/>
                </div>
            </div>
        </div>
    );
}
